import type {
	FetchIdentificationInfoResponse,
	UpdateCitizenshipRequest,
	UpdateIdentificationInfoRequest,
	UpdateResidenceRequest,
} from '@rocket-ui/lk-api';
import {isNonRussiaPersonalInfoForm} from './countries/oksmCode';
import type {PersonalInfoStepValues} from './types';

/**
 * «Давайте познакомимся»: «Фамилия/Имя (на русском)» —
 * Армения, Беларусь, Казахстан, Украина, Киргизия и все прочие, кроме РФ.
 */
export const requiresRussianNameLabels = (
	citizenshipOksmCode: string | null | undefined,
	_residenceOksmCode?: string | null | undefined,
): boolean => {
	return isNonRussiaPersonalInfoForm(citizenshipOksmCode);
};

const readOksmCode = (value: unknown): string | null => {
	if (!value || typeof value !== 'object') {
		return null;
	}

	const oksmCode = (value as {oksmCode?: unknown}).oksmCode;

	return typeof oksmCode === 'string' && oksmCode.trim() ? oksmCode.trim() : null;
};

const readText = (value: string | null | undefined): string => value?.trim() ?? '';

/** GET /identification/info → значения шага «Давайте познакомимся» (названия стран — отдельно, по справочнику). */
export const mapIdentificationInfoToPersonalInfo = (
	data: FetchIdentificationInfoResponse | undefined,
): Partial<PersonalInfoStepValues> => {
	const info = data?.info;
	const result: Partial<PersonalInfoStepValues> = {};

	const citizenshipOksmCode = readOksmCode(data?.citizenship);
	const residenceOksmCode = readOksmCode(data?.residence);

	if (citizenshipOksmCode) {
		result.citizenshipOksmCode = citizenshipOksmCode;
	}

	if (residenceOksmCode) {
		result.residenceOksmCode = residenceOksmCode;
	}

	if (info?.gender === 'male' || info?.gender === 'female') {
		result.gender = info.gender;
	}

	const secondName = readText(info?.secondName);
	const firstName = readText(info?.firstName);
	const patronymic = readText(info?.patronymic);
	const email = readText(info?.email);

	if (secondName) result.secondName = secondName;
	if (firstName) result.firstName = firstName;
	if (patronymic) result.patronymic = patronymic;
	if (email) result.email = email;

	return result;
};

export const buildIdentificationInfoRequest = (
	values: PersonalInfoStepValues,
): UpdateIdentificationInfoRequest | null => {
	if (!values.gender) {
		return null;
	}

	const patronymic = values.patronymic;

	return {
		gender: values.gender,
		secondName: values.secondName,
		firstName: values.firstName,
		patronymic: patronymic || null,
		email: values.email,
	};
};

export const buildCitizenshipRequest = (values: PersonalInfoStepValues): UpdateCitizenshipRequest | null => {
	if (!values.citizenshipOksmCode) {
		return null;
	}

	return {oksmCode: values.citizenshipOksmCode};
};

export const buildResidenceRequest = (values: PersonalInfoStepValues): UpdateResidenceRequest | null => {
	if (!values.residenceOksmCode) {
		return null;
	}

	return {oksmCode: values.residenceOksmCode};
};
